import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { taxonomiesApi } from '../api/taxonomiesApi'
import type { TaxonomyGroup, TaxonomyTerm } from '../types'
import type { RootState } from '@/store/store'

interface ContentTaxonomyState {
  taxonomies: TaxonomyGroup[]
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  lastFetchedAt: number | null
}

const initialState: ContentTaxonomyState = {
  taxonomies: [],
  status: 'idle',
  error: null,
  lastFetchedAt: null,
}

// Fetch all taxonomies with caching. Will skip network call if already loaded or in-flight unless forceRefresh is true.
export const fetchContentTaxonomies = createAsyncThunk<
  TaxonomyGroup[],
  { forceRefresh?: boolean } | undefined,
  { state: RootState }
>(
  'contentTaxonomy/fetchAll',
  async () => {
    return await taxonomiesApi.listTaxonomies()
  },
  {
    condition: (options, { getState }) => {
      if (options?.forceRefresh) return true
      const { contentTaxonomies } = getState()
      if (
        contentTaxonomies.status === 'loading' ||
        (contentTaxonomies.status === 'succeeded' && contentTaxonomies.taxonomies.length > 0)
      ) {
        return false
      }
      return true
    },
  }
)

export const createTaxonomyGroupThunk = createAsyncThunk<
  TaxonomyGroup,
  { code: string; name: string; description?: string; translations?: Record<string, any>; entity_types?: string[] }
>('contentTaxonomy/createGroup', async (payload) => {
  return await taxonomiesApi.createTaxonomy(payload)
})

export const updateTaxonomyGroupThunk = createAsyncThunk<
  TaxonomyGroup,
  { id: string; payload: { name?: string; description?: string; translations?: Record<string, any>; entity_types?: string[] } }
>('contentTaxonomy/updateGroup', async ({ id, payload }) => {
  return await taxonomiesApi.updateTaxonomy(id, payload)
})

export const deleteTaxonomyGroupThunk = createAsyncThunk<string, string>(
  'contentTaxonomy/deleteGroup',
  async (id) => {
    await taxonomiesApi.removeTaxonomy(id)
    return id
  }
)

export const createTaxonomyTermThunk = createAsyncThunk<
  TaxonomyTerm,
  { taxonomyId: string; payload: { code: string; name: string; description?: string; translations?: Record<string, any>; parent_id?: string; sort_order?: number } }
>('contentTaxonomy/createTerm', async ({ taxonomyId, payload }) => {
  return await taxonomiesApi.createTerm(taxonomyId, payload)
})

export const updateTaxonomyTermThunk = createAsyncThunk<
  TaxonomyTerm,
  { termId: string; payload: { name?: string; description?: string; translations?: Record<string, any>; parent_id?: string; sort_order?: number } }
>('contentTaxonomy/updateTerm', async ({ termId, payload }) => {
  return await taxonomiesApi.updateTerm(termId, payload)
})

export const deleteTaxonomyTermThunk = createAsyncThunk<
  { taxonomyId: string; termId: string },
  { taxonomyId: string; termId: string }
>('contentTaxonomy/deleteTerm', async ({ taxonomyId, termId }) => {
  await taxonomiesApi.removeTerm(termId)
  return { taxonomyId, termId }
})

export const contentTaxonomySlice = createSlice({
  name: 'contentTaxonomy',
  initialState,
  reducers: {
    invalidateTaxonomiesCache: (state) => {
      state.status = 'idle'
      state.lastFetchedAt = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Taxonomies
      .addCase(fetchContentTaxonomies.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchContentTaxonomies.fulfilled, (state, action: PayloadAction<TaxonomyGroup[]>) => {
        state.status = 'succeeded'
        state.taxonomies = action.payload
        state.lastFetchedAt = Date.now()
      })
      .addCase(fetchContentTaxonomies.rejected, (state, action) => {
        if (action.meta.condition) return
        state.status = 'failed'
        state.error = action.error.message || 'Không thể tải danh sách taxonomy.'
      })
      // Create Group
      .addCase(createTaxonomyGroupThunk.fulfilled, (state, action: PayloadAction<TaxonomyGroup>) => {
        state.taxonomies.push(action.payload)
      })
      // Update Group
      .addCase(updateTaxonomyGroupThunk.fulfilled, (state, action: PayloadAction<TaxonomyGroup>) => {
        const index = state.taxonomies.findIndex((g) => g.id === action.payload.id)
        if (index !== -1) {
          state.taxonomies[index] = action.payload
        }
      })
      // Delete Group
      .addCase(deleteTaxonomyGroupThunk.fulfilled, (state, action: PayloadAction<string>) => {
        state.taxonomies = state.taxonomies.filter((g) => g.id !== action.payload)
      })
      // Create Term
      .addCase(createTaxonomyTermThunk.fulfilled, (state, action: PayloadAction<TaxonomyTerm>) => {
        const group = state.taxonomies.find((g) => g.id === action.payload.taxonomy_id)
        if (group) {
          if (!group.terms) group.terms = []
          group.terms.push(action.payload)
        }
      })
      // Update Term
      .addCase(updateTaxonomyTermThunk.fulfilled, (state, action: PayloadAction<TaxonomyTerm>) => {
        const group = state.taxonomies.find((g) => g.id === action.payload.taxonomy_id)
        if (group && group.terms) {
          const index = group.terms.findIndex((t) => t.id === action.payload.id)
          if (index !== -1) {
            group.terms[index] = action.payload
          }
        }
      })
      // Delete Term
      .addCase(deleteTaxonomyTermThunk.fulfilled, (state, action) => {
        const { taxonomyId, termId } = action.payload
        const group = state.taxonomies.find((g) => g.id === taxonomyId)
        if (group && group.terms) {
          group.terms = group.terms.filter((t) => t.id !== termId)
        }
      })
  },
})

export const { invalidateTaxonomiesCache } = contentTaxonomySlice.actions

export const selectContentTaxonomies = (state: RootState) => state.contentTaxonomies?.taxonomies || []

export const selectContentTaxonomiesByScope = (scope?: string) => (state: RootState) => {
  const all = state.contentTaxonomies?.taxonomies || []
  if (!scope || scope === 'all') return all
  return all.filter((g) => (g.scopes || []).includes(scope) || (g.scopes || []).includes('all'))
}

export const selectContentTaxonomyStatus = (state: RootState) => state.contentTaxonomies?.status || 'idle'

export default contentTaxonomySlice.reducer
